import { useState, useEffect } from 'react'
import { Modal, message } from 'antd'
import { TextLink } from '@weareberyl/design-system'

import {
  useZoneEnableMutation,
  useZoneSuspendWithReasonMutation,
  ZoneSuspensionInfoInput,
} from '~/gql/generated/graphql'
import type { SetAnyLoading } from '../useIsAnyLoading'

import { SuspendModal } from '~/components/Zone/SuspendModal'

const confirm = Modal.confirm
const SUSPEND_SYMBOL = Symbol('suspendZone')
const ENABLE_SYMBOL = Symbol('enableZone')

type BaseProps = {
  id: string
  setAnyLoading: SetAnyLoading
}

const SuspendBay = ({ id, setAnyLoading }: BaseProps) => {
  const [modalOpen, setModalOpen] = useState(false)

  const [suspendZone, { loading: suspendLoading }] =
    useZoneSuspendWithReasonMutation({
      onCompleted: () => message.success('Bay suspended'),
      onError: () =>
        message.error(
          'Something went wrong when trying to change the suspended status',
        ),
      refetchQueries: ['ZoneSummary'],
    })

  useEffect(
    () => setAnyLoading(SUSPEND_SYMBOL, suspendLoading),
    [suspendLoading, setAnyLoading],
  )

  const handleConfirm = (info: ZoneSuspensionInfoInput) => {
    suspendZone({
      variables: { id, suspension_reason: info },
    })
  }

  return (
    <>
      <TextLink
        variant="grape"
        size="small"
        onPress={() => setModalOpen(true)}
        testID="bay-suspend-btn"
      >
        Suspend Bay
      </TextLink>

      <SuspendModal
        open={modalOpen}
        count={1}
        onCancel={() => setModalOpen(false)}
        onConfirm={info => {
          setModalOpen(false)
          handleConfirm(info)
        }}
      />
    </>
  )
}

const EnableBay = ({ id, setAnyLoading }: BaseProps) => {
  const [enableZone, { loading: enableLoading }] = useZoneEnableMutation({
    variables: { id },
    onCompleted: () => message.success('Bay enabled'),
    onError: () =>
      message.error(
        'Something went wrong when trying to change the suspended status',
      ),
    refetchQueries: ['ZoneSummary'],
  })

  useEffect(
    () => setAnyLoading(ENABLE_SYMBOL, enableLoading),
    [enableLoading, setAnyLoading],
  )

  const showEnableModal = () =>
    confirm({
      zIndex: 1050,
      title: 'Enable this bay?',
      onOk: enableZone,
    })

  return (
    <TextLink
      variant="grape"
      size="small"
      onPress={showEnableModal}
      testID="bay-enable-btn"
    >
      Enable Bay
    </TextLink>
  )
}

export type SuspendZoneProps = BaseProps & { isSuspended: boolean }

export const SuspendZone = ({
  isSuspended,
  ...baseProps
}: SuspendZoneProps) => {
  return isSuspended ? (
    <EnableBay {...baseProps} />
  ) : (
    <SuspendBay {...baseProps} />
  )
}
